'use client';

import { useMemo, useState } from 'react';
import { bionicToHtml, SAMPLE_BIONIC_TEXT } from '@/lib/browser-textanalysis-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserBionicReadingConverter() {
  const [text, setText] = useState('');
  const [fixation, setFixation] = useState(3);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const html = useMemo(() => bionicToHtml(text, fixation), [text, fixation]);
  const hasInput = text.trim().length > 0;

  async function handleCopy() {
    try {
      await copyText(html);
      setCopyMessage('Bionic HTML copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the HTML.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Bionic reading</span>
        <h2 className="tool-live-title">Bionic Reading Converter</h2>
        <p className="tool-live-subtitle">
          Bold the leading letters of every word to guide your eyes and read faster. Adjust the
          fixation strength and copy the formatted HTML.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Your text</span>
              <textarea
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste or type the text you want to convert..."
                rows={12}
                value={text}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Fixation strength: {fixation} of 5</span>
              <input
                max={5}
                min={1}
                onChange={(e) => setFixation(Number(e.target.value))}
                step={1}
                type="range"
                value={fixation}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => setText(SAMPLE_BIONIC_TEXT)}
              type="button"
            >
              Load sample
            </button>
            <button
              className="button button-secondary"
              disabled={!hasInput}
              onClick={handleCopy}
              type="button"
            >
              Copy HTML
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            style={{ fontSize: '0.72rem', color: 'var(--muted, #94a3b8)', marginBottom: '0.4rem' }}
          >
            Preview
          </div>
          <div
            style={{
              border: '1px solid var(--border, rgba(148,163,184,0.25))',
              borderRadius: '0.5rem',
              padding: '0.9rem 1rem',
              minHeight: '10rem',
              fontSize: '1.05rem',
              lineHeight: 1.7,
              maxHeight: '24rem',
              overflow: 'auto',
            }}
          >
            {hasInput ? (
              <div dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              <p style={{ color: 'var(--muted, #94a3b8)', margin: 0 }}>
                Enter text to see the bionic-reading preview.
              </p>
            )}
          </div>
          {hasInput ? (
            <label
              className="tool-live-field tool-live-field-full"
              style={{ marginTop: '0.75rem' }}
            >
              <span>HTML output</span>
              <textarea readOnly rows={5} value={html} />
            </label>
          ) : null}
          <p className="tool-live-panel-note">Formatted locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
